import React from 'react';
import Modal from 'react-bootstrap/Modal';
import './LoadingScreenModal.css';

// Standardised loading screen: an animated bus driving in place.
// Motion is done with SVG SMIL (<animateTransform>/<animate>) so rotation
// happens around an exact centre and runs reliably everywhere — no reliance
// on CSS transform-box/transform-origin.
const Wheel = ({ cx, cy }) => (
  <g>
    <circle cx={cx} cy={cy} r="17" fill="#ffffff" />
    <circle cx={cx} cy={cy} r="13" fill="#0a2f6b" />
    <g stroke="#cdd9ee" strokeWidth="2.5" strokeLinecap="round">
      <line x1={cx} y1={cy - 11} x2={cx} y2={cy + 11} />
      <line x1={cx - 11} y1={cy} x2={cx + 11} y2={cy} />
    </g>
    <circle cx={cx} cy={cy - 9} r="2.6" fill="#FFC400" />
    <circle cx={cx} cy={cy} r="4.2" fill="#FFC400" />
    <animateTransform
      attributeName="transform"
      attributeType="XML"
      type="rotate"
      from={`0 ${cx} ${cy}`}
      to={`360 ${cx} ${cy}`}
      dur="0.5s"
      repeatCount="indefinite"
    />
  </g>
);

const Streak = ({ x1, y, x2, begin }) => (
  <line x1={x1} y1={y} x2={x2} y2={y} opacity="0">
    <animateTransform
      attributeName="transform"
      attributeType="XML"
      type="translate"
      from="34 0"
      to="-30 0"
      dur="0.8s"
      begin={begin}
      repeatCount="indefinite"
    />
    <animate
      attributeName="opacity"
      values="0;1;1;0"
      keyTimes="0;0.2;0.65;1"
      dur="0.8s"
      begin={begin}
      repeatCount="indefinite"
    />
  </line>
);

const Cloud = ({ x, y, scale = 1, fill = '#ffffff', dur, drift = 16 }) => (
  <g transform={`translate(${x} ${y}) scale(${scale})`} fill={fill}>
    <g>
      <circle cx="12" cy="14" r="12" />
      <circle cx="29" cy="6" r="16" />
      <circle cx="47" cy="14" r="12" />
      <rect x="8" y="14" width="42" height="14" rx="7" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="translate"
        values={`${drift * 0.7} 0; ${-drift} 0; ${drift * 0.7} 0`}
        dur={dur}
        repeatCount="indefinite"
      />
    </g>
  </g>
);

export const LoadingScreenModal = ({ message = 'Putting your trip together' }) => {
  return (
    <Modal
      show={true}
      dialogClassName="bus-loader-dialog"
      contentClassName="bus-loader-content"
      aria-labelledby="LoadingScreenModal"
      animation={false}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Body className="p-0">
        <div className="bus-loader">
          <svg
            className="bus-loader__svg"
            viewBox="0 0 480 250"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            {/* clouds */}
            <Cloud x="396" y="108" scale="0.8" fill="#d4e6fb" dur="6.5s" drift={12} />
            <Cloud x="46" y="44" scale="1" dur="5s" drift={18} />
            <Cloud x="344" y="34" scale="0.85" dur="6s" drift={14} />

            {/* speed streaks whooshing past, left of the bus */}
            <g stroke="#a9cdf2" strokeWidth="5" strokeLinecap="round">
              <Streak x1="120" x2="158" y="118" begin="0s" />
              <Streak x1="100" x2="150" y="135" begin="-0.27s" />
              <Streak x1="122" x2="156" y="152" begin="-0.54s" />
            </g>

            {/* bus (bounces) */}
            <g>
              <rect x="175" y="112" width="172" height="66" rx="16" fill="#FFC400" />
              <rect x="192" y="124" width="28" height="24" rx="4" fill="#0a2f6b" />
              <rect x="228" y="124" width="28" height="24" rx="4" fill="#0a2f6b" />
              <rect x="264" y="124" width="28" height="24" rx="4" fill="#0a2f6b" />
              <path d="M300,124 h26 a10,10 0 0 1 10,10 v14 h-36 z" fill="#0a2f6b" />
              <circle cx="342" cy="167" r="4" fill="#ffffff" />
              <Wheel cx={216} cy={182} />
              <Wheel cx={306} cy={182} />
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="translate"
                values="0 0; 0 -5; 0 0; 0 -2; 0 0"
                keyTimes="0;0.25;0.5;0.75;1"
                dur="0.55s"
                repeatCount="indefinite"
              />
            </g>

            {/* moving road */}
            <line
              x1="118"
              y1="206"
              x2="362"
              y2="206"
              stroke="#9fc6f0"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="20 16"
            >
              <animate
                attributeName="stroke-dashoffset"
                from="0"
                to="-36"
                dur="0.4s"
                repeatCount="indefinite"
              />
            </line>
          </svg>

          <p className="bus-loader__text">{message}</p>
        </div>
      </Modal.Body>
    </Modal>
  );
};
